import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, FilePlus, Upload } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { createDocument, uploadDocument } from "../../../api/hrm/documents/info";
import { getDocumentGroups } from "../../../api/hrm/documents/groups";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import DocumentAccessPicker from "./DocumentAccessPicker";

/** Nhóm tài liệu = thư mục gốc (parent_id null) */
const buildFolderOptions = (groups = []) =>
  groups
    .filter((g) => !g.parent_id)
    .map((g) => ({ value: g.id, label: g.group_name }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label), "vi"));

/** Phân loại = thư mục con thuộc một Nhóm tài liệu */
const buildCategoryOptions = (groups = [], folderId) => {
  if (!folderId) return [];
  return groups
    .filter((g) => g.parent_id === folderId)
    .map((g) => ({ value: g.id, label: g.group_name }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label), "vi"));
};

const resolveFolderAndCategory = (groups, selectedId) => {
  if (!selectedId) return { folderId: "", categoryId: "" };
  const node = groups.find((g) => g.id === selectedId);
  if (!node) return { folderId: selectedId, categoryId: "" };
  if (node.parent_id) {
    return { folderId: node.parent_id, categoryId: node.id };
  }
  return { folderId: node.id, categoryId: "" };
};

const FieldLabel = ({ children, required }) => (
  <label className="text-sm font-medium text-gray-700 block mb-1">
    {children}
    {required ? <span className="text-red-500"> *</span> : null}
  </label>
);

const inputClass =
  "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100";

const DocumentCreateModal = ({ isOpen, onClose, onSaved, defaultGroupId = "" }) => {
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [issuer, setIssuer] = useState("");
  const [tags, setTags] = useState("");
  const [folderId, setFolderId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [groups, setGroups] = useState([]);
  const [accessAll, setAccessAll] = useState(true);
  const [accessBranchIds, setAccessBranchIds] = useState([]);
  const [accessDepartmentIds, setAccessDepartmentIds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getDocumentGroups();
        const items = Array.isArray(res) ? res : res?.items || res?.data || [];
        if (cancelled) return;
        setGroups(items);
        const resolved = resolveFolderAndCategory(items, defaultGroupId || "");
        setFolderId(resolved.folderId);
        setCategoryId(resolved.categoryId);
      } catch {
        if (!cancelled) {
          setGroups([]);
          showErrorToast("Không thể tải danh sách nhóm tài liệu!");
        }
      }
    })();
    setAccessAll(true);
    setAccessBranchIds([]);
    setAccessDepartmentIds([]);
    return () => {
      cancelled = true;
    };
  }, [isOpen, defaultGroupId]);

  const folderOptions = useMemo(() => buildFolderOptions(groups), [groups]);
  const categoryOptions = useMemo(
    () => buildCategoryOptions(groups, folderId),
    [groups, folderId]
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      showErrorToast("Chỉ chấp nhận file PDF!");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showErrorToast("Dung lượng tối đa là 25MB!");
      return;
    }
    setFileData(file);
    setFileName(file.name.replace(/\.[^/.]+$/, ""));
  };

  const resetForm = () => {
    setFileData(null);
    setFileName("");
    setDescription("");
    setIssueDate("");
    setEffectiveFrom("");
    setEffectiveTo("");
    setIssuer("");
    setTags("");
    setFolderId("");
    setCategoryId("");
    setAccessAll(true);
    setAccessBranchIds([]);
    setAccessDepartmentIds([]);
  };

  const handleSave = async () => {
    if (!fileData) return showErrorToast("Vui lòng chọn tệp tài liệu!");
    if (!description.trim()) return showErrorToast("Vui lòng nhập mô tả!");
    if (!issueDate) return showErrorToast("Vui lòng chọn ngày ban hành!");
    if (!issuer) return showErrorToast("Vui lòng nhập cơ sở ban hành!");
    if (
      effectiveFrom &&
      effectiveTo &&
      new Date(effectiveFrom) > new Date(effectiveTo)
    ) {
      return showErrorToast("Khoảng ngày hiệu lực không hợp lệ.");
    }
    if (
      !accessAll &&
      accessBranchIds.length === 0 &&
      accessDepartmentIds.length === 0
    ) {
      return showErrorToast(
        "Hãy chọn ít nhất một Chi nhánh hoặc Bộ phận được xem, hoặc tick «Tất cả»."
      );
    }

    const confirm = await showConfirmDialog({
      title: "Xác nhận lưu",
      text: "Bạn có chắc chắn muốn lưu tài liệu này?",
      confirmText: "Lưu",
      cancelText: "Hủy",
    });
    if (!confirm.isConfirmed) return;

    const formData = new FormData();
    formData.append("file", fileData);

    try {
      setLoading(true);
      const uploadRes = await uploadDocument(formData);
      const { file_url, file_type, file_size } = uploadRes;

      // Ưu tiên Phân loại (con); nếu không chọn thì gắn vào Nhóm tài liệu (cha)
      const finalGroupId = categoryId || folderId || null;
      const folder = groups.find((g) => g.id === folderId);
      const category = groups.find((g) => g.id === categoryId);
      const groupName = category
        ? `${folder?.group_name || ""} / ${category.group_name}`.replace(
            /^\s*\/\s*/,
            ""
          )
        : folder?.group_name || null;

      await createDocument({
        file_name: fileName,
        file_url,
        file_type,
        file_size,
        description,
        issue_date: issueDate,
        effective_from: effectiveFrom,
        effective_to: effectiveTo,
        issuer,
        tags,
        status: "active",
        group_id: finalGroupId,
        group_name: groupName,
        access_all: accessAll,
        access_branch_ids: accessAll ? [] : accessBranchIds,
        access_department_ids: accessAll ? [] : accessDepartmentIds,
      });

      showSuccessToast("Đã lưu tài liệu thành công!");
      resetForm();
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("❌ Lỗi khi lưu tài liệu:", err);
      showErrorToast("Không thể lưu tài liệu.");
    } finally {
      setLoading(false);
    }
  };

  if (typeof window === "undefined" || !window.document?.body) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9990]"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4"
          >
            <div className="bg-white rounded-t-[2rem] sm:rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden max-h-[94dvh] sm:max-h-[min(92vh,900px)]">
              {/* Header */}
              <div className="shrink-0 flex justify-between items-center px-5 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50/80 via-white to-white">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-800">
                  <FilePlus className="w-5 h-5 text-red-600" />
                  Thêm tài liệu mới
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="hover:bg-gray-100 rounded-full p-2 transition"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body — 2 cột */}
              <div className="flex-1 overflow-y-auto p-5 sm:p-6 custom-scrollbar">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-6">
                  {/* Cột 1: metadata + phạm vi */}
                  <div className="lg:col-span-3 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <FieldLabel>Nhóm tài liệu</FieldLabel>
                        <Select
                          options={folderOptions}
                          styles={customSelectStyles}
                          placeholder="-- Chọn nhóm (thư mục cha) --"
                          isClearable
                          isSearchable
                          value={
                            folderId
                              ? folderOptions.find((o) => o.value === folderId) ||
                                null
                              : null
                          }
                          onChange={(opt) => {
                            setFolderId(opt?.value || "");
                            setCategoryId("");
                          }}
                        />
                      </div>
                      <div>
                        <FieldLabel>Phân loại</FieldLabel>
                        <Select
                          options={categoryOptions}
                          styles={customSelectStyles}
                          placeholder={
                            folderId
                              ? categoryOptions.length
                                ? "-- Chọn phân loại (con) --"
                                : "Nhóm chưa có phân loại"
                              : "Chọn Nhóm tài liệu trước"
                          }
                          isClearable
                          isSearchable
                          isDisabled={!folderId}
                          value={
                            categoryId
                              ? categoryOptions.find(
                                  (o) => o.value === categoryId
                                ) || null
                              : null
                          }
                          onChange={(opt) => setCategoryId(opt?.value || "")}
                        />
                      </div>
                    </div>

                    <div>
                      <FieldLabel required>Mô tả nội dung</FieldLabel>
                      <textarea
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Tóm tắt nội dung tài liệu..."
                        className={inputClass}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <FieldLabel required>Ngày ban hành</FieldLabel>
                        <input
                          type="date"
                          value={issueDate}
                          onChange={(e) => setIssueDate(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel required>Cơ sở ban hành</FieldLabel>
                        <input
                          type="text"
                          value={issuer}
                          onChange={(e) => setIssuer(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Hiệu lực từ ngày</FieldLabel>
                        <input
                          type="date"
                          value={effectiveFrom}
                          onChange={(e) => setEffectiveFrom(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Hiệu lực đến ngày</FieldLabel>
                        <input
                          type="date"
                          value={effectiveTo}
                          onChange={(e) => setEffectiveTo(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                    </div>

                    <DocumentAccessPicker
                      accessAll={accessAll}
                      branchIds={accessBranchIds}
                      departmentIds={accessDepartmentIds}
                      disabled={loading}
                      onChange={({
                        accessAll: nextAll,
                        branchIds,
                        departmentIds,
                      }) => {
                        setAccessAll(nextAll);
                        setAccessBranchIds(branchIds);
                        setAccessDepartmentIds(departmentIds);
                      }}
                    />
                  </div>

                  {/* Cột 2: tệp + tags — không stretch theo cột trái */}
                  <div className="lg:col-span-2 space-y-4 self-start w-full">
                    <div className="rounded-2xl border border-gray-100 bg-slate-50/60 p-4 flex flex-col gap-4">
                      <div>
                        <FieldLabel required>Tệp tài liệu</FieldLabel>
                        <div
                          className={`h-44 border-2 border-dashed rounded-xl p-4 text-center text-sm flex flex-col items-center justify-center gap-1.5 transition overflow-hidden ${
                            loading
                              ? "bg-gray-100 text-gray-400 border-gray-200"
                              : "bg-white border-gray-200 hover:border-red-300 hover:bg-red-50/30"
                          }`}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (file)
                              handleFileSelect({ target: { files: [file] } });
                          }}
                        >
                          <input
                            type="file"
                            accept="application/pdf"
                            id="document-file-input"
                            className="hidden"
                            onChange={handleFileSelect}
                            disabled={loading}
                          />
                          <div className="h-12 w-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-1">
                            <Upload size={22} />
                          </div>
                          <label
                            htmlFor="document-file-input"
                            className={`font-semibold cursor-pointer ${
                              loading
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-red-600 hover:underline"
                            }`}
                          >
                            Chọn tệp PDF để tải lên
                          </label>
                          <p className="text-xs text-gray-500">
                            Kéo thả vào đây · tối đa 25 MB
                          </p>
                          {fileData && (
                            <div className="mt-3 w-full rounded-xl border border-red-100 bg-red-50/80 px-3 py-2.5 text-left">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {fileData.name}
                              </p>
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <span className="text-xs text-gray-500">
                                  {(fileData.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFileData(null);
                                    setFileName("");
                                  }}
                                  className="text-xs font-semibold text-red-600 hover:underline"
                                >
                                  Xóa tệp
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <FieldLabel>Tags (từ khóa tìm kiếm)</FieldLabel>
                        <input
                          type="text"
                          value={tags}
                          onChange={(e) => setTags(e.target.value)}
                          placeholder="VD: nội quy, quy chế, đào tạo…"
                          className={inputClass}
                        />
                        <p className="mt-1 text-[11px] text-gray-400">
                          Cách nhau bằng dấu phẩy để dễ tìm sau này.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="shrink-0 flex justify-end gap-3 px-5 sm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-100 bg-gray-50/80">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className={`px-6 py-2.5 rounded-xl font-semibold text-sm text-white transition ${
                    loading
                      ? "bg-red-400 cursor-not-allowed"
                      : "bg-red-600 hover:bg-red-700 shadow-md shadow-red-200"
                  }`}
                >
                  {loading ? "Đang lưu…" : "Lưu tài liệu"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    window.document.body
  );
};

export default DocumentCreateModal;
